import {Button} from "antd";
import {UserAddOutlined} from "@ant-design/icons";
import React, {Component} from "react";
import TeamMemberDialog from "./TeamMemberDialog";
import User from "../../types/User";
import UserService from "../../services/UserService";
import UserContainer from "./UserContainer";
import {FormType} from "../../types/FormType";
import RolesService from "../../services/RolesService";
import {errorNotification, successNotification} from "../../services/NotificationService";
import {Subject} from "rxjs";
import {temporarySubscription} from "../../services/Helpers";
import {TokenState} from "../../services/TokenState";
import styles from "./Team.module.css";
import pageStyles from "../apps/Apps.module.css";

interface TeamPageState {
    isLoaded: boolean
    isMemberDialogOpen: boolean
    user?: User
    users: User[]
    roles: string[]
}

export default class TeamPage extends Component<{}, TeamPageState> {

    unsubscribe$ = new Subject<void>();

    state: TeamPageState = {
        isLoaded: false,
        isMemberDialogOpen: false,
        user: undefined,
        users: [],
        roles: []
    }

    componentDidMount() {
        temporarySubscription(UserService.users$, this.unsubscribe$).subscribe((users) => {
            this.setState({users, isLoaded: true})
        })
        temporarySubscription(RolesService.$roles, this.unsubscribe$).subscribe(roles => {
            this.setState({roles})
        })
        UserService.loadUsers()
        RolesService.loadRoles()
    }

    componentWillUnmount() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    onUserDeleteHandler = (user: User) => {
        UserService.deleteUser(user.id as string).then(() => {
            UserService.loadUsers()
            successNotification(`${user.username} removed from the team`)
        }).catch(reason => {
            console.error(reason)
            errorNotification(`${user.username} couldn't be removed. Try again.`)
        })
    }

    render() {
        const {users, isLoaded} = this.state;
        const self = TokenState.token()?.sub;
        return <section aria-labelledby="team-title">
            <div className={pageStyles.pageHead}>
                <div>
                    <h1 id="team-title" className={pageStyles.pageTitle}>Team</h1>
                    <p className={pageStyles.pageLede}>
                        Everyone who can sign in to App Repo. Admins can upload, release and remove builds and manage the team.
                    </p>
                </div>
                <Button type="primary" icon={<UserAddOutlined/>}
                        onClick={() => this.setState({isMemberDialogOpen: true, user: undefined})}>
                    Add member
                </Button>
            </div>

            <div className={styles.listHead}>
                <span>Members</span>
                <span className="mono">{isLoaded ? users.length : "–"}</span>
            </div>

            {isLoaded && users.length === 0 && (
                <p className={styles.empty}>No members yet. Add someone so they can sign in.</p>
            )}

            {!isLoaded && (
                <p className={styles.empty} aria-busy="true">Loading members…</p>
            )}

            <ul className={styles.list}>
                {users.map(user => (
                    <UserContainer key={user.id ?? user.username} user={user}
                                   isSelf={user.username === self}
                                   onEdit={(u) => this.setState({isMemberDialogOpen: true, user: u})}
                                   onDelete={this.onUserDeleteHandler}/>
                ))}
            </ul>

            {this.state.isMemberDialogOpen && (
                <TeamMemberDialog roles={this.state.roles} type={this.state.user ? FormType.EDIT : FormType.CREATE}
                                  user={this.state.user}
                                  onClose={() => this.setState({isMemberDialogOpen: false})}/>
            )}
        </section>
    }
}
